import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DAILY_ITEM_STATES } from './fleetUtils';

const DailyCheckDialog = ({ vehicle, requiresReason, defaultReason, open, onClose, onValidated }) => {
  const [phase, setPhase] = useState('form');
  const [reason, setReason] = useState('');
  const [check, setCheck] = useState(null);
  const [template, setTemplate] = useState(null);
  const [results, setResults] = useState({});
  const [mileage, setMileage] = useState('');
  const [justification, setJustification] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const start = useCallback(async (recheckReason) => {
    setError(null);
    try {
      const res = await axios.post(`${API}/fleet/daily/checks`, {
        vehicle_id: vehicle.vehicle_id,
        recheck_reason: recheckReason || null,
      }, { withCredentials: true });
      setCheck(res.data);
      setTemplate(res.data.template);
      setResults(res.data.attempt?.results || {});
      setMileage(res.data.attempt?.mileage ?? '');
      setPhase('form');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Erreur';
      if (detail.startsWith('recheck_reason_required')) {
        setPhase('reason');
      } else {
        setError(detail);
        setPhase('error');
      }
    }
  }, [vehicle]);

  useEffect(() => {
    if (!open || !vehicle) return;
    setDone(null); setError(null); setResults({}); setMileage(''); setJustification('');
    if (requiresReason) {
      setReason(defaultReason || '');
      setPhase('reason');
    } else {
      start(null);
    }
  }, [open, vehicle, requiresReason, defaultReason, start]);

  if (!vehicle) return null;

  const setState = (key, state) => setResults(r => ({ ...r, [key]: { ...(r[key] || {}), state } }));
  const setField = (key, field, value) => setResults(r => ({ ...r, [key]: { ...(r[key] || {}), [field]: value } }));
  const allConforme = () => {
    const next = {};
    (template?.categories || []).forEach(c => { next[c.key] = { state: 'conforme' }; });
    setResults(next);
  };

  const filled = (template?.categories || []).filter(c => results[c.key]?.state).length;
  const hasUncontrolled = (template?.categories || []).some(c => (results[c.key]?.state || 'non_controle') === 'non_controle');

  const validate = async () => {
    setSubmitting(true); setError(null);
    try {
      const n = check.attempt.attempt_number;
      await axios.put(`${API}/fleet/daily/checks/${check.daily_check_id}/attempts/${n}`, {
        results, mileage: mileage === '' ? null : parseInt(mileage, 10),
      }, { withCredentials: true });
      const res = await axios.post(`${API}/fleet/daily/checks/${check.daily_check_id}/attempts/${n}/validate`, {
        uncontrolled_justification: justification || null,
      }, { withCredentials: true });
      setDone(res.data);
      onValidated?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="daily-check-dialog">
        <DialogHeader>
          <DialogTitle>Contrôle quotidien rapide — {vehicle.plate_number}</DialogTitle>
        </DialogHeader>

        {phase === 'reason' && !done && (
          <div className="space-y-3" data-testid="daily-recheck-reason-step">
            <p className="text-sm text-gray-600">Un contrôle a déjà été validé aujourd'hui pour ce véhicule. Indiquez la raison du re-contrôle.</p>
            <div>
              <Label>Raison du re-contrôle *</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Ex : remise en service après réparation" data-testid="daily-recheck-reason-input" />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button disabled={!reason.trim()} onClick={() => start(reason.trim())} data-testid="daily-recheck-start-btn">
              Démarrer le re-contrôle
            </Button>
          </div>
        )}

        {phase === 'error' && !done && (
          <p className="text-sm text-red-600" data-testid="daily-check-error">{error}</p>
        )}

        {phase === 'form' && check && !done && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-gray-600">Renseignés : <strong>{filled}/10</strong>
                {check.attempt.attempt_number > 1 && <span className="ml-2 text-purple-700 font-medium">(tentative n°{check.attempt.attempt_number})</span>}
              </p>
              <Button size="sm" variant="outline" onClick={allConforme} data-testid="daily-all-conforme-btn">
                ✅ Tout est conforme
              </Button>
            </div>

            <div className="space-y-2">
              {(template?.categories || []).map((cat) => {
                const r = results[cat.key] || {};
                return (
                  <div key={cat.key} className="border rounded-lg p-3 space-y-2" data-testid={`daily-cat-${cat.key}`}>
                    <p className="text-sm font-medium">{cat.label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {DAILY_ITEM_STATES.map(s => (
                        <button key={s.value} type="button" onClick={() => setState(cat.key, s.value)}
                          className={`text-xs px-2.5 py-1.5 rounded font-medium border ${r.state === s.value ? s.cls : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                          data-testid={`daily-${cat.key}-${s.value}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                    {r.state === 'probleme' && (
                      <div className="space-y-2 bg-red-50 border border-red-200 rounded p-2">
                        <Textarea value={r.description || ''} onChange={(e) => setField(cat.key, 'description', e.target.value)}
                          placeholder="Décrivez le problème constaté *" rows={2}
                          data-testid={`daily-${cat.key}-description`} />
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setField(cat.key, 'severity', 'non_bloquant')}
                            className={`text-xs px-2.5 py-1.5 rounded font-medium border ${r.severity === 'non_bloquant' ? 'bg-yellow-500 text-white' : 'bg-white text-gray-600'}`}
                            data-testid={`daily-${cat.key}-non-bloquant`}>
                            Non bloquant
                          </button>
                          <button type="button" onClick={() => setField(cat.key, 'severity', 'potentiellement_bloquant')}
                            className={`text-xs px-2.5 py-1.5 rounded font-medium border ${r.severity === 'potentiellement_bloquant' ? 'bg-red-600 text-white' : 'bg-white text-gray-600'}`}
                            data-testid={`daily-${cat.key}-potentiellement-bloquant`}>
                            ⚠️ Potentiellement bloquant
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div>
              <Label>Kilométrage constaté (optionnel)</Label>
              <Input type="number" min="0" value={mileage} onChange={(e) => setMileage(e.target.value)}
                placeholder="Ex : 45230" data-testid="daily-mileage-input" />
            </div>

            {hasUncontrolled && (
              <div>
                <Label>Justification des éléments « Non contrôlé » *</Label>
                <Textarea value={justification} onChange={(e) => setJustification(e.target.value)}
                  placeholder="Expliquez pourquoi certains points n'ont pas été contrôlés" rows={2}
                  data-testid="daily-uncontrolled-justification" />
              </div>
            )}

            {error && <p className="text-sm text-red-600" data-testid="daily-validate-error">{error}</p>}

            <Button className="w-full" onClick={validate} disabled={submitting} data-testid="daily-validate-btn">
              {submitting ? 'Validation...' : 'Valider le contrôle quotidien'}
            </Button>
          </div>
        )}

        {done && (
          <div className="space-y-3 text-center py-4" data-testid="daily-check-result">
            {done.outcome === 'conforme' && (
              <p className="text-green-700 font-medium">✅ Contrôle validé — véhicule conforme et utilisable.</p>
            )}
            {done.outcome === 'a_surveiller' && (
              <p className="text-yellow-700 font-medium">🟠 Contrôle validé — problème(s) non bloquant(s) : le véhicule reste utilisable, une anomalie a été créée.</p>
            )}
            {done.outcome === 'attente_decision' && (
              <p className="text-purple-700 font-medium">🛑 Problème potentiellement bloquant : le véhicule est en attente de décision sécurité et ne peut plus être sélectionné. Le chef de parc a été alerté.</p>
            )}
            <Button onClick={onClose} data-testid="daily-result-close-btn">Fermer</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DailyCheckDialog;
